import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { ArrowRight, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { Toaster } from "@/components/ui/sonner";
import { Doodle, Starburst } from "@/components/marketing/Decor";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      const dest = (loc.state && loc.state.from) || "/vendor/dashboard";
      navigate(dest, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-light mkt-root mkt-paper-noise min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <Toaster richColors position="top-center" />
      <Doodle.Star className="hidden md:block absolute left-8 top-10 h-10 w-10 text-black/80" />
      <Doodle.Crown className="hidden md:block absolute right-12 top-8 h-12 w-16 text-[var(--graffiti-yellow-deep)] -rotate-6" />
      <Doodle.Bolt className="hidden md:block absolute right-10 bottom-10 h-12 w-8 text-black rotate-12" />
      <Doodle.Scribble className="hidden md:block absolute left-6 bottom-16 h-10 w-24 text-black/70 -rotate-6" />
      <Starburst className="absolute left-1/4 top-1/3 h-5 w-5 text-black/30 hidden md:block" />

      <div className="w-full max-w-md relative">
        <div className="flex justify-center mb-6">
          <Link to="/" aria-label="CityDooh home">
            <Logo size={150} light />
          </Link>
        </div>

        <div className="mkt-sticker p-7 relative">
          <span className="mkt-tape absolute -top-4 left-6" data-testid="login-eyebrow">Vendor Login</span>
          <h1 className="font-display uppercase text-3xl mt-3 leading-none">
            Welcome <span className="mkt-highlight">back</span>
          </h1>
          <p className="text-sm text-black/70 mt-2">Sign in to your CityDooh vendor dashboard.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label className="uppercase text-[11px] font-bold tracking-wider text-black">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="h-11 border-2 border-black bg-white"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="uppercase text-[11px] font-bold tracking-wider text-black">Password</Label>
                <Link
                  to="/vendor/forgot-password"
                  className="text-xs font-bold text-black underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2 hover:text-[var(--graffiti-yellow-deep)]"
                  data-testid="login-forgot-link"
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="h-11 border-2 border-black bg-white"
              />
            </div>
            {err && (
              <p className="text-sm font-bold text-[#8b1212] bg-[#fbd5d5] border-2 border-[#8b1212] rounded-md px-3 py-2" data-testid="login-error">
                {err}
              </p>
            )}
            <button type="submit" className="mkt-btn w-full justify-center" disabled={busy} data-testid="login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Sign in <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </form>

          <p className="text-sm text-black/70 mt-5 text-center">
            New here?{" "}
            <Link to="/vendor/register" className="font-bold text-black underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
